"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useFilterParser = void 0;
const peggy = __importStar(require("peggy"));
const react_1 = require("react");
const assert_1 = require("../../../utils/assert");
const event_callback_hook_1 = require("../../../utils/event-callback-hook");
const update_effect_1 = require("../../../utils/update-effect");
const grammar_1 = require("./grammar");
const highlight_hook_1 = require("./hooks/highlight-hook");
const input_hook_1 = require("./hooks/input-hook");
const keyboard_events_hook_1 = require("./hooks/keyboard-events-hook");
const suggestion_hook_1 = require("./hooks/suggestion-hook");
const utils_1 = require("./utils");
function useFilterParser(props) {
    const { isFilterBox, columns, setFilters, text, setText } = props;
    /**
     * Reference to parser
     */
    const parser = (0, react_1.useRef)();
    (0, react_1.useEffect)(() => {
        if (isFilterBox) {
            parser.current = peggy.generate(grammar_1.grammar, { trace: true });
        }
        else {
            parser.current = undefined;
        }
    }, [isFilterBox]);
    /**
     * Aliases to accept in filterbox column labels that can be easily converted into column keys.
     */
    const aliases = (0, react_1.useRef)([]);
    /**
     * Display red border of filterbox in case of invalid expression.
     */
    const [error, setError] = (0, react_1.useState)(false);
    (0, react_1.useEffect)(() => {
        aliases.current = (0, utils_1.createAliases)(columns);
    }, [columns]);
    /**
     * Filterbox input value converted into EAS filters.
     */
    const [internalState, setInternalState] = (0, react_1.useState)([]);
    const { inputRef, inputWidth, cursorPosition, inputScrollOffset } = (0, input_hook_1.useInput)();
    const { highlightedText, setHighlights, handleHighlight } = (0, highlight_hook_1.useHighlight)({
        text,
        inputScrollOffset,
        inputWidth,
        isFilterBox,
    });
    const { listRef, focusedTermPosition, suggestions, setSuggestions, clearSuggestions, handleFieldSuggestion, handleValueSuggestion, } = (0, suggestion_hook_1.useSuggestion)({
        cursorPosition: cursorPosition.current,
        aliases: aliases.current,
    });
    const { handleKeyDown, handleInput } = (0, keyboard_events_hook_1.useKeyboardEvents)({
        inputEl: inputRef.current,
        listEl: listRef.current,
        focusedTermPosition: focusedTermPosition.current,
        submit: () => setFilters(internalState),
        setText,
    });
    /**
     * Main parser function.
     */
    const parse = (0, event_callback_hook_1.useEventCallback)(() => {
        try {
            if (parser.current) {
                const parsed = parser.current.parse(text, {
                    tracer: {
                        trace: (event) => {
                            handleFieldSuggestion(event);
                            handleHighlight(event);
                            handleValueSuggestion(event);
                        },
                    },
                });
                setError(false);
                const filters = (0, utils_1.createFilters)([parsed], aliases.current);
                setInternalState(filters);
            }
        }
        catch (e) {
            setError(text !== '');
            // console.log(e.expected, e.found, e.location);
        }
    });
    const handleEvent = (0, react_1.useCallback)((e) => {
        const target = e.target;
        (0, assert_1.assertNonNullish)(target.selectionStart, 'Unexpected error, input is not focused.');
        cursorPosition.current = target.selectionStart;
        focusedTermPosition.current.start = 0;
        focusedTermPosition.current.end = 0;
        setHighlights([]);
        setSuggestions([]);
        setInternalState([]);
        parse();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const handleClear = (0, event_callback_hook_1.useEventCallback)(() => {
        setHighlights([]);
        setSuggestions([]);
        setInternalState([]);
        setText('');
        setFilters((value) => {
            value.length = 0;
            return value;
        });
        setError(false);
        cursorPosition.current = 0;
        focusedTermPosition.current.start = 0;
        focusedTermPosition.current.end = 0;
    });
    const handleSubmit = (0, event_callback_hook_1.useEventCallback)(() => {
        setFilters(internalState);
    });
    (0, utils_1.useEventListener)({
        target: inputRef.current,
        types: ['pointerup', 'keyup'],
        handler: handleEvent,
        shouldRegister: isFilterBox,
    });
    /**
     * Init filterbox.
     */
    (0, update_effect_1.useUpdateEffect)(() => {
        if (!isFilterBox) {
            handleClear();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFilterBox]);
    return {
        listRef,
        inputRef,
        inputWidth,
        inputScrollOffset,
        cursorPosition,
        highlightedText,
        suggestions,
        clearSuggestions,
        error,
        handleKeyDown,
        handleInput,
        handleClear,
        handleSubmit,
    };
}
exports.useFilterParser = useFilterParser;
//# sourceMappingURL=filter-box.js.map